import React from 'react';
import {
    Box,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslate } from 'react-admin';

interface CapabilitiesFormProps {
    capabilities: any;
    onChange: (capabilities: any) => void;
    disabled?: boolean;
}

export const CapabilitiesForm: React.FC<CapabilitiesFormProps> = ({
    capabilities = {},
    onChange,
    disabled = false,
}) => {
    const translate = useTranslate();

    const handleBooleanChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({
            ...capabilities,
            [field]: event.target.checked,
        });
    };

    const handleNumberChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        onChange({
            ...capabilities,
            [field]: value ? parseInt(value, 10) : undefined,
        });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
                {translate('resources.ocpp-servers.capabilities.description')}
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                }}
            >
                {/* Boolean Capabilities */}
                <FormControlLabel
                    control={
                        <Switch
                            checked={capabilities?.supportsChargingProfiles || false}
                            onChange={handleBooleanChange('supportsChargingProfiles')}
                            disabled={disabled}
                            color="primary"
                        />
                    }
                    label={translate('resources.ocpp-servers.capabilities.supportsChargingProfiles')}
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={capabilities?.supportsLMS || false}
                            onChange={handleBooleanChange('supportsLMS')}
                            disabled={disabled}
                            color="primary"
                        />
                    }
                    label={translate('resources.ocpp-servers.capabilities.supportsLMS')}
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={capabilities?.supportsRemoteCommands || false}
                            onChange={handleBooleanChange('supportsRemoteCommands')}
                            disabled={disabled}
                            color="primary"
                        />
                    }
                    label={translate('resources.ocpp-servers.capabilities.supportsRemoteCommands')}
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={capabilities?.supportsSmartCharging || false}
                            onChange={handleBooleanChange('supportsSmartCharging')}
                            disabled={disabled}
                            color="primary"
                        />
                    }
                    label={translate('resources.ocpp-servers.capabilities.supportsSmartCharging')}
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={capabilities?.supportsFirmwareUpdate || false}
                            onChange={handleBooleanChange('supportsFirmwareUpdate')}
                            disabled={disabled}
                            color="primary"
                        />
                    }
                    label={translate('resources.ocpp-servers.capabilities.supportsFirmwareUpdate')}
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={capabilities?.supportsLocalAuthList || false}
                            onChange={handleBooleanChange('supportsLocalAuthList')}
                            disabled={disabled}
                            color="primary"
                        />
                    }
                    label={translate('resources.ocpp-servers.capabilities.supportsLocalAuthList')}
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={capabilities?.supportsReservations || false}
                            onChange={handleBooleanChange('supportsReservations')}
                            disabled={disabled}
                            color="primary"
                        />
                    }
                    label={translate('resources.ocpp-servers.capabilities.supportsReservations')}
                />

                {/* Numeric Capabilities */}
                <TextField
                    label={translate('resources.ocpp-servers.capabilities.maxConcurrentSessions')}
                    type="number"
                    value={capabilities?.maxConcurrentSessions || ''}
                    onChange={handleNumberChange('maxConcurrentSessions')}
                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                    disabled={disabled}
                    fullWidth
                    inputProps={{ min: 1, max: 10000 }}
                    helperText={translate('resources.ocpp-servers.capabilities.maxConcurrentSessionsHelper')}
                />
            </Box>
        </Box>
    );
};